import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {useLocation, useNavigate} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { save } from "../redux/form4Slice";
import { save as saveForm1 } from "../redux/form1Slice";
import { save as saveForm2 } from "../redux/form2Slice";
import { save as saveForm3 } from "../redux/form3Slice";
import { RootState } from "../redux/store";
import { InputsForm4 } from "../types/InputsForm4";
import { useTravelPrice } from "../utils/useTravelPrice";
import { useTranslation, Trans } from "react-i18next";
import { useApi } from "../utils/useApi";
import { priceResponce } from "../types/price";

//MainTemplate
import MainTemplate from "./MainTemplate";
import SectionRetry from "./SectionRetry";

//scss
import "../scss/common.scss";
import "../scss/component.scss";

import { Backdrop, CircularProgress } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';

//parts import
import FormStep from "./Form/FormStep";
import {
  SectionPayment
} from "./section/Form4"
import SectionTravelPrice from "./section/SectionTravelPrice";
import {PlanSelectFormData} from "../types/planSelectForm";
import {getPriceRequestParams} from "../utils/price";
import {creditAPI, getCreditRequestParams} from "../utils/credit";
import {isOption} from "../utils/reserve";
import {devLog} from "../utils/errors";
import {getHeadTitle} from "../utils/title";
import {FormSessionContext} from "../hooks/FormSession";
import {TagManagerContext, TagManagerType} from "../hooks/GTM";
import { PartsTutorialButton } from "./parts";
import {langPath} from "../utils/lang";


// lodingのスタイル
const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));


const Form4: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tagManagerContext: TagManagerType = useContext(TagManagerContext);
  const {checkSession, loadFormData} = useContext(FormSessionContext);
  const classesForLoading = useStyles();
  const location = useLocation()
  const [planSelectFormData, setPlanSelectFormData] = useState<{ data: PlanSelectFormData }>(location.state as { data: PlanSelectFormData }) //TODO: need change

  // 料金データをローカルstateで管理
  const [priceDataState, setPriceDataState] = useState<priceResponce | undefined>(undefined);
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);
  /** サーバー指示の待ち秒数（サーキットブレーカの遮断残り時間）。 2026-09-07 */
  const [priceRetryAfterSec, setPriceRetryAfterSec] = useState<number | undefined>(undefined);

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  // reduxに保存されているデータをデバッグ
  // for debug
  const savedState = useSelector((state: RootState) => state);
  devLog("redux form1〜3", {
    ...savedState.form1,
    ...savedState.form2,
    ...savedState.form3,
  });

  const savedForm1 = savedState.form1.form1;
  const savedForm3 = savedState.form3.form3;
  devLog("savedForm3", savedForm3)

  const navigate = useNavigate();

  const dispatch = useDispatch();

  /**
   * sessionStorage から復元 + 料金API（/price）呼び出し。
   *
   * ★初回 useEffect と「再試行」ボタンの両方から呼べるよう関数に切り出した
   *   （issue #2992 / 報告書 §8 No.5）。
   *   従来は useEffect 内のローカル関数で、失敗すると再取得の手段が無く
   *   「料金が空欄のまま画面に留まる」だけだった（docs/99 隠れ仕様 #27）。
   *   `/price` はホテル→航空→アクティビティ→オプション→レンタカーを**逐次**呼ぶため、
   *   混雑時は 120 秒のタイムアウトに達しやすい。
   */
  const restoreAndFetchPrice = useCallback(async () => {
    setPriceLoading(true);
    setPriceError(null);
    setPriceRetryAfterSec(undefined);

    // sessionStorageから復元
    const savedData1 = loadFormData('form1');
    const savedData2 = loadFormData('form2');
    const savedData3 = loadFormData('form3');

    // Redux更新
    if (savedData1) {
      dispatch(saveForm1({ form1: savedData1 }));
    }
    if (savedData2) {
      dispatch(saveForm2({ form2: savedData2 }));
    }
    if (savedData3) {
      dispatch(saveForm3({ form3: savedData3 }));
    }

    // 料金API呼び出し（復元データを使用）
    try {
      const params = getPriceRequestParams(
        planSelectFormData.data,
        savedData1 || savedState.form1.form1,
        savedData3 || savedState.form3.form3
      );

      const response = await useApi.post({
        url: "/price",
        data: params
      });

      const priceData = response.data as priceResponce;

      if (priceData.status === 200) {
        setPriceDataState(priceData);
      } else {
        setPriceError("料金の取得に失敗しました。");
        setPriceRetryAfterSec((priceData as {retryAfterSec?: number})?.retryAfterSec);
      }
    } catch (error) {
      setPriceError("APIの呼び出しに失敗しました。");
      setPriceRetryAfterSec(
          (error as {response?: {data?: {retryAfterSec?: number}}})?.response?.data?.retryAfterSec);
    }

    setPriceLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    restoreAndFetchPrice();
  }, [restoreAndFetchPrice]);

  /**
   * 取得に時間がかかっているときの補助文（issue #2992 / 報告書 §8 No.5）。
   * `/price` の上限は 120 秒。無言で待たせると「固まった」と受け取られるため 30 秒で案内する。
   */
  const [isSlowPrice, setIsSlowPrice] = useState(false);
  const slowTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!priceLoading) {
      setIsSlowPrice(false);
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
      }
      return;
    }
    slowTimerRef.current = setTimeout(() => setIsSlowPrice(true), 30000);
    return () => {
      if (slowTimerRef.current) {
        clearTimeout(slowTimerRef.current);
      }
    };
  }, [priceLoading]);

  const {
    control,
    formState: { isSubmitting, errors },
    getValues,
    handleSubmit,
    register,
  } = useForm<InputsForm4>({
  });

  const [creditLoading, setCreditLoading] = useState(false)
  const [isCreditError,setIsCreditError] = useState(false);
  const [isFreeCredit, setIsFreeCredit] = useState(true);
  const onSubmit: SubmitHandler<any> = async (data) => {
    const session = checkSession(planSelectFormData.data);
    if (!session) {
      return;
    }
    
    setCreditLoading(true);
    const creditRequestParams = getCreditRequestParams(planSelectFormData.data, savedForm1, savedForm3, data);
    const resultCredit = await creditAPI(creditRequestParams);
    setCreditLoading(false);
    if (resultCredit.status !== 200) {
      // 与信エラー
      setIsCreditError(true)
      window.scrollTo(0, 0)
    } else if (resultCredit.data !== null)  {
      dispatch(save({ form4: {
        payment: {
          cashcard_number: data.payment?.cashcard_number ?? "",
          cashcard_month: data.payment?.cashcard_month ?? "",
          cashcard_year: data.payment?.cashcard_year ?? "",
          cashcard_name: data.payment?.cashcard_name ?? "",
          cashcard_number_of_payments: data.payment?.cashcard_number_of_payments ?? "",
          cashcard_cvc: data.payment?.cashcard_cvc ?? "",
          token: resultCredit.responce.paymentId,
        }
        }}));
      planSelectFormData.data.priceData = {status: 200, result: resultCredit.responce.resultPlanPrice};
      
      navigate(langPath(i18n.language)+"/Form5",{ state: {
          data: planSelectFormData.data
        } });
    }
    
    
  };

  // 料金データはuseEffectで設定される
  const priceData = priceDataState;
  const isLoading = priceLoading;
  const isError = priceError !== null;
  const error = priceError;

  if (isError) {
    console.log("error", error);
  }

  useEffect(() => {
    if(priceData?.status === 200) {
      planSelectFormData.data.travelPrice = priceData.result.insurance.insuranceCancelAmount;
      tagManagerContext.pageView(getHeadTitle(planSelectFormData.data, "決済情報の入力"))
      if (priceData.result.taxTotalPrice > 0) {
        setIsFreeCredit(false);
      }
    }
    // ★旧 `else if (priceData && isError)`（alert → プランページへ強制遷移）は削除した。
    //   priceData が入るのは status 200 のときだけで priceError は null なので**到達不能**だった
    //   （issue #2992 / 報告書 §8 No.5）。失敗時は画面に留めて「再試行」を出す。
  }, [priceData])
  // todo フォームデータチェック（ない場合はstep１に戻る、プランセレクトデータがない場合は404）


  // 次へ進むボタンのラベルの数字
  const buttonIconLabel = () => {
    if (i18n.language === 'ja') {
      if (!isOption(planSelectFormData.data)) {
        return  "4";
      }
      
      return "5";
    }
    
    if (!isOption(planSelectFormData.data)) {
      return  "4";
    }
    
    return "3";
  }
  
  // 前に戻るボタンのラベル
  const backStepButtonLabel = () => {
    console.log("planSelectFormData.data in backStepButtonLabel", planSelectFormData.data);
    if (i18n.language === 'ja') {
      return t("保険加入の選択に戻る");
    }
    
    if (isOption(planSelectFormData.data)) {
      return t("オプション情報入力に戻る");
    }
    
    return t("お客様情報入力に戻る");
  }
  
  return (
      <MainTemplate title={getHeadTitle(planSelectFormData.data, "決済情報の入力")} planSelectFormData={planSelectFormData.data}>
        
        {/* ローディングの表示(APIのレスポンス待ち) */}
        <Backdrop className={classesForLoading.backdrop} open={isLoading || creditLoading}>
          <CircularProgress color="inherit" />
        </Backdrop>

        <div className="l-page-content-upper_navi">
          <div className="wrap-content-900">
            <h1 className="l-page-content-upper_navi-heading">
              {t("決済情報の入力")}
            </h1>
          </div>
        </div>

        <div className="l-page-content">
          <div className="wrap-content-900">
           <FormStep activeStep={4} isOption={isOption(planSelectFormData.data)}></FormStep>

           {/* 操作ガイドボタン */}
           <PartsTutorialButton value="component-content" />

            <form action="" onSubmit={handleSubmit(onSubmit)}>
              {/* <!--アラート--> */}
              {isCreditError ?
                <>
                  <div className="wrap-content-900-no_padding">
                    <div className="box-alert-red-small icon-alert">
                      <p className="fz-14 fz-16-md c-red">
                        {t("クレジットカードエラー")}
                      </p>
                      <p className="fz-14">
                        <Trans i18nKey={"ご入力いただいたカード情報（カード番号、有効期限、セキュリティコード）に誤りがないかご確認ください。入力内容に誤りがない場合、カードの有効期限が切れているか、利用限度額を超過している可能性があります。詳しくは、ご利用のカード会社へお問い合わせください。"} components={{br: <br/>}} />
                      </p>
                    </div>
                  </div>
                </>:
                <>
                  <div className="box-border-blue small icon-alert">
                    <p className="fz-16 c-blue">
                      {t("旅行代金をご確認のうえ、決済情報を入力してください。")}
                    </p>
                  </div>
                </>
              }

              {/*
                * 料金取得の失敗（issue #2992 / 報告書 §8 No.5）。
                * ★従来はエラー処理が `// todo` のままで、料金が空欄の画面に留まるだけだった
                *   （docs/99 隠れ仕様 #27）。案内と再試行手段を出す。
                */}
              {isError && !isLoading && (
                <div className="wrap-content-900-no_padding mb-24">
                  {/*
                    * ★共通の `SectionRetry` に揃える（2026-09-07 再レビュー）。
                    *   即座に押せるボタンだと、失敗した利用者が**同じ瞬間に一斉再試行**して
                    *   上流が回復する前にまた飽和する。サーバー指示の待ち秒数
                    *   （`retryAfterSec`）＋ 10〜40 秒のランダムを挟む。
                    */}
                  <SectionRetry
                    retryAfterSec={priceRetryAfterSec}
                    isLoading={isLoading}
                    onRetry={() => { restoreAndFetchPrice(); }}
                    message={t("料金を取得できませんでした。混雑している可能性があります。") ?? undefined}
                    buttonLabel={t("料金を再取得する") ?? undefined}
                  />
                </div>
              )}

              {/* 取得に時間がかかっている場合の補助文（issue #2992） */}
              {isLoading && isSlowPrice && (
                <div className="wrap-content-900-no_padding mb-24">
                  <p className="fz-14 c-blue">
                    {t("料金の計算に時間がかかっています。そのままお待ちください。")}
                  </p>
                </div>
              )}

              {/* 旅行代金 */}
              <SectionTravelPrice
                priceData={priceData}
              />

              {/*お支払い方法 */}
              {!isFreeCredit && (
                <SectionPayment
                  register={register}
                  getValues={getValues}
                  control={control}
                  errors={errors}
                >
                </SectionPayment>
              )}

              {/* <!--次へ進む--> */}
              <div className="mt-48 mt-64-md">
                <ul className="form-step_button mt-24">
                  <li className="form-step_button-next">
                    {/* <a href="/Form2/" className="button-large-red">
                      <span className="label-number-orange mr-5">5</span>
                      予約の最終確認をする
                    </a> */}
                    <button
                      type="submit"
                      className="button-large-red"
                      // todo: class作る
                      style={{
                        display: "block",
                        border: "none",
                        width: "100%",
                      }}
                      // disabled={!isDirty || isSubmitting} // note: これだとhistory.back()で戻ったときにdisabledになる
                      // ★料金が未取得の間は進ませない（issue #2992 / 報告書 §8 No.5）。
                      //   従来は料金空欄のまま「次へ」を押せ、/payment 側で再計算していたため
                      //   利用者は自分が何を承諾したのか分からないまま進めてしまえた。
                      disabled={isSubmitting || !priceDataState}
                    >
                      <span className="label-number-orange mr-5">{`${buttonIconLabel()}`}</span>
                      {t("予約の最終確認へ進む")}
                    </button>
                  </li>
                  <li className="form-step_button-back">
                    <button
                      type="button"
                      className="button-small-border_gray_blue button-medium-md"
                      onClick={() => {
                        window.history.back();
                      }}
                    >
                      <i className="icon-left_arrow mr-5"></i>
                      {`${backStepButtonLabel()}`}
                    </button>
                  </li>
                </ul>
              </div>
            </form>
          </div>
        </div>

    </MainTemplate>
  );
};

export default Form4;
